import type { KeyboardEvent, MouseEvent, PropsWithChildren } from 'react'
import React from 'react'
import EllipsisItem from './EllipsisItem'
import PageItem from './PageItem'
import type {
  PaginationColor,
  PaginationSize,
  PaginationVariant,
} from './Pagination'

interface PageListProps {
  /**
   * Number of pages to show at the start and end of the pagination
   */
  boundaryCount: number
  /**
   * Total number of pages
   */
  count: number
  /**
   * The color scheme of the pagination component
   */
  color: PaginationColor
  /**
   * If true, the page list is disabled
   */
  disabled: boolean
  /**
   * Callback fired when a page item is activated
   */
  onClick: (event: MouseEvent | KeyboardEvent) => void
  /**
   * The current selected page number.
   * Used to determine if the current page is selected
   */
  page: number
  /**
   * Number of pages to show before and after the current page
   */
  siblingCount: number
  /**
   * The size of the page list component
   */
  size: PaginationSize
  /**
   * The visual style variant of the page list component
   */
  variant: PaginationVariant
}

function PageList({
  boundaryCount,
  count,
  color,
  disabled,
  page,
  onClick,
  siblingCount,
  size,
  variant,
}: PropsWithChildren<PageListProps>): JSX.Element {
  const pageList = []
  const numBoundaryDisplay = 2 * siblingCount + boundaryCount + 2
  const ellipseLeft = page - 1 - siblingCount - boundaryCount >= 2
  const ellipseRight = count - page - siblingCount - boundaryCount >= 2
  const showAllPages = count - numBoundaryDisplay - boundaryCount < 2

  if (showAllPages) {
    for (let index = 1; index <= count; index++) {
      pageList.push(
        <PageItem
          color={color}
          disabled={disabled}
          index={index}
          key={`coconut-pagination-${index}`}
          onClick={onClick}
          page={page}
          size={size}
          variant={variant}
        />,
      )
    }
  } else {
    if (ellipseLeft) {
      for (let index = 1; index <= boundaryCount; index++) {
        pageList.push(
          <PageItem
            color={color}
            disabled={disabled}
            index={index}
            key={`coconut-pagination-${index}`}
            onClick={onClick}
            page={page}
            size={size}
            variant={variant}
          />,
        )
      }
      pageList.push(
        <EllipsisItem
          disabled={disabled}
          key="coconut-pagination-ellipsis-left"
          size={size}
        />,
      )
    } else {
      for (let index = 1; index <= numBoundaryDisplay; index++) {
        pageList.push(
          <PageItem
            color={color}
            disabled={disabled}
            index={index}
            key={`coconut-pagination-${index}`}
            onClick={onClick}
            page={page}
            size={size}
            variant={variant}
          />,
        )
      }
    }

    if (ellipseLeft && ellipseRight) {
      for (
        let index = page - siblingCount;
        index <= page + siblingCount;
        index++
      ) {
        pageList.push(
          <PageItem
            color={color}
            disabled={disabled}
            index={index}
            key={`coconut-pagination-${index}`}
            onClick={onClick}
            page={page}
            size={size}
            variant={variant}
          />,
        )
      }
    }

    if (ellipseRight) {
      pageList.push(
        <EllipsisItem
          disabled={disabled}
          key="coconut-pagination-ellipsis-right"
          size={size}
        />,
      )
      for (let index = count + 1 - boundaryCount; index <= count; index++) {
        pageList.push(
          <PageItem
            color={color}
            disabled={disabled}
            index={index}
            key={`coconut-pagination-${index}`}
            onClick={onClick}
            page={page}
            size={size}
            variant={variant}
          />,
        )
      }
    } else {
      for (
        let index = count + 1 - numBoundaryDisplay;
        index <= count;
        index++
      ) {
        pageList.push(
          <PageItem
            color={color}
            disabled={disabled}
            index={index}
            key={`coconut-pagination-${index}`}
            onClick={onClick}
            page={page}
            size={size}
            variant={variant}
          />,
        )
      }
    }
  }

  return <> {pageList} </>
}

export default PageList
